import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Hero from '../components/Hero';
import FilterSection from '../components/FilterSection';
import HotJobs from '../components/HotJobs';
import { LegacyCity, LegacyJob } from '../types';
import {
  trackCityClicked,
  trackHotJobClicked,
  trackRemoteBannerClicked,
  trackHomebasedBannerClicked,
  trackCtaClicked,
  trackSearchQuery,
} from '../src/lib/analytics';

// 使用 Legacy 类型用于向后兼容
type City = LegacyCity;
type Job = LegacyJob;

const HomePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleSelectCity = (city: City) => {
    trackCityClicked(city.id, city.name, 'homepage');
    navigate(`/cities/${city.id}`);
  };

  const handleSelectJob = (job: Job) => {
    trackHotJobClicked(job.id, job.title, 0);
    navigate(`/jobs/${job.id}`);
  };

  const handleShowAllJobs = () => {
    trackCtaClicked('primary');
    navigate('/jobs');
  };

  const handleShowAllCities = () => {
    navigate('/offices');
  };

  const handleShowRemoteJobs = () => {
    trackRemoteBannerClicked();
    navigate('/jobs?type=INTERNSHIP_ONLINE');
  };

  const handleShowHomeBasedJobs = () => {
    trackHomebasedBannerClicked();
    navigate('/jobs?workStyle=HOME_BASED');
  };

  const handleSearchJobsWithQuery = (query: string) => {
    trackSearchQuery(query, 0);
    navigate(`/jobs?q=${encodeURIComponent(query)}`);
  };

  return (
    <>
      <Hero 
        onExplore={handleShowAllJobs} 
        onLife={() => navigate('/life')} 
      />
      <FilterSection
        onSelectCity={handleSelectCity}
        onSelectJob={handleSelectJob}
        onShowAllJobs={handleShowAllJobs}
        onShowAllCities={handleShowAllCities}
        onShowRemoteJobs={handleShowRemoteJobs}
        onShowHomeBasedJobs={handleShowHomeBasedJobs}
        onSearchJobsWithQuery={handleSearchJobsWithQuery}
      />
      <HotJobs
        onViewAll={handleShowAllJobs}
        onSelectJob={handleSelectJob}
      />

      {/* CTA Section - 人才库招募 */}
      <section className="bg-gradient-to-br from-slate-50 to-blue-50 pt-6 sm:pt-8 pb-8 sm:pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mx-auto text-center">
            {/* 标题 */}
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-4 sm:mb-6">
              {t('cta.title', '没找到合适的职位？')}
            </h2>
            <p className="text-base sm:text-lg text-slate-600 mb-8 sm:mb-10 leading-relaxed max-w-xl mx-auto">
              {t('cta.description', '提交您的简历到我们的人才库，当有合适职位开放时，我们会主动联系您。')}
            </p>

            {/* CTA 按钮 */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => { trackCtaClicked('primary'); navigate('/jobs'); }}
                className="w-full sm:w-auto bg-slate-900 text-white px-8 sm:px-10 py-3.5 sm:py-4 rounded-xl font-bold text-base sm:text-lg hover:bg-blue-600 active:bg-blue-700 transition-all shadow-lg shadow-slate-900/20 cursor-pointer min-h-[52px]"
              >
                {t('cta.primaryBtn', '查看所有职位')}
              </button>
              <button
                onClick={() => { trackCtaClicked('secondary'); navigate('/life'); }}
                className="w-full sm:w-auto bg-white text-slate-700 px-8 sm:px-10 py-3.5 sm:py-4 rounded-xl font-bold text-base sm:text-lg hover:bg-slate-100 hover:text-slate-900 active:bg-slate-200 transition-all border border-slate-200 cursor-pointer min-h-[52px]"
              >
                {t('cta.secondaryBtn', '了解我们')}
              </button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default HomePage;
